import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import WfLoader from '@app/src/components/WfLoader'
import {
  SCHEDULE_SCREENING_QUESTION_KEYS,
  ScheduleScreeningFormData,
} from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleScreening/ScheduleProviderScreeningModal'
import { Response } from '@app/src/types/resourceExplorer'
import { uniq } from '@app/src/utils/helpers'
import { ExpandMoreOutlined, InfoOutlined } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Chip,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useConfirm } from 'material-ui-confirm'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

interface ReportedDataFoundStepProps {
  alreadyExistingReportedData: Response[]
  alreadyExistingReportedDataLoading: boolean
  widthOverride?: number
}

const ReportedDataFoundStep: React.FC<ReportedDataFoundStepProps> = ({
  widthOverride,
  alreadyExistingReportedData,
  alreadyExistingReportedDataLoading,
}) => {
  const { formatMessage } = useIntl()
  const confirm = useConfirm()
  const { watch } = useFormContext<ScheduleScreeningFormData>()

  const watchProviders = watch(SCHEDULE_SCREENING_QUESTION_KEYS.PROVIDER_IDS)
  const watchTemplates = watch(SCHEDULE_SCREENING_QUESTION_KEYS.TEMPLATES)

  if (alreadyExistingReportedDataLoading) return <WfLoader />

  const allDataWeWantToScrapeAlreadyExists =
    watchProviders.length * watchTemplates.length === alreadyExistingReportedData.length

  const responsesByProviderId = Object.values(
    Object.groupBy(alreadyExistingReportedData ?? [], g => g.request.subscriptions?.[0]?.targetProviderId),
  ).map(s => s ?? [])

  return (
    <CreationModalStep
      title={formatMessage({ id: 'form.scheduleProviderScreening.reportedDataFound.title' })}
      extraButtons={[
        {
          label: formatMessage({ id: 'general.learnMore' }),
          variant: 'text',
          onClick: () =>
            confirm({
              title: formatMessage({ id: 'form.scheduleProviderScreening.reportedDataFound.dialogTitle' }),
              description: formatMessage({
                id: 'form.scheduleProviderScreening.reportedDataFound.dialogDescription',
              }),
              confirmationText: formatMessage({ id: 'general.close' }),
              hideCancelButton: true,
            }),
        },
      ]}
      nextButtonTooltip={
        allDataWeWantToScrapeAlreadyExists
          ? formatMessage({
              id: 'form.scheduleProviderScreening.reportedDataFound.descriptionAllScreenedAlreadyReported',
            })
          : undefined
      }
      description={formatMessage({
        id: allDataWeWantToScrapeAlreadyExists
          ? 'form.scheduleProviderScreening.reportedDataFound.descriptionAllScreenedAlreadyReported'
          : 'form.scheduleProviderScreening.reportedDataFound.description',
      })}
      fieldnames={[]}
      widthOverride={widthOverride}
      canGoNext={() => !allDataWeWantToScrapeAlreadyExists}
    >
      <Accordion
        elevation={0}
        disableGutters
        sx={[{ bgcolor: 'grey.100', '&:before': { position: 'unset' } }]}
        defaultExpanded
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlined fontSize="medium" sx={{ mx: 1 }} />} sx={{ py: 1 }}>
          <Stack direction="row">
            <InfoOutlined />
            <Typography variant="body1" ml={3}>
              {formatMessage({ id: 'statistics.companiesCount' }, { count: responsesByProviderId.length })}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List disablePadding>
            {responsesByProviderId.map(responses => {
              const provider = responses?.[0].request.subscriptions?.[0].target
              const uniqueTemplatesNames = uniq(responses?.map(res => res.request.title) ?? [])
              return (
                <ListItem
                  key={provider.id}
                  sx={{
                    py: 2,
                    borderTop: `1px solid ${alpha('#000', 0.12)}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1} width="60%">
                    <CompanyCell company={provider} disableCell />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Chip label={uniqueTemplatesNames[0]} />
                    {uniqueTemplatesNames.length > 1 && (
                      <Tooltip title={uniqueTemplatesNames.slice(1).join(', ')}>
                        <Chip label={`+ ${uniqueTemplatesNames.length - 1}`} />
                      </Tooltip>
                    )}
                  </Stack>
                </ListItem>
              )
            })}
          </List>
        </AccordionDetails>
      </Accordion>
    </CreationModalStep>
  )
}

export default ReportedDataFoundStep
